import React, { createContext, useContext, useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';
import { tryRefreshAccessToken } from '../utils/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check if user is authenticated on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const accessToken = localStorage.getItem('accessToken');

    if (!accessToken) {
      setLoading(false);
      return;
    }

    const fetchMe = async () => {
      const token = localStorage.getItem('accessToken');
      return fetch(`${API_BASE_URL}/api/auth/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    };

    try {
      let response = await fetchMe();

      if (response.status === 401) {
        const refreshed = await tryRefreshAccessToken();
        if (refreshed) {
          response = await fetchMe();
        }
      }

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        setPermissions(userData.permissions || []);
        setRoles(userData.roles || []);
        setIsAuthenticated(true);
      } else if (response.status === 401) {
        logout();
      } else {
        console.warn('Auth check failed with status', response.status);
      }
    } catch (error) {
      // Network blips should not clear a still-valid session
      console.error('Auth check failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Login failed');
      }

      const data = await response.json();
      
      // Store tokens
      localStorage.setItem('accessToken', data.access_token);
      localStorage.setItem('refreshToken', data.refresh_token);
      
      // Set user data
      setUser(data.user);
      setPermissions(data.user.permissions || []);
      setRoles(data.user.roles || []);
      setIsAuthenticated(true);
      
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const logout = async () => {
    const accessToken = localStorage.getItem('accessToken');
    
    // Call logout endpoint for audit logging
    if (accessToken) {
      try {
        await fetch(`${API_BASE_URL}/api/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`
          }
        });
      } catch (error) {
        console.error('Logout API call failed:', error);
      }
    }
    
    // Clear local state and storage
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    setUser(null);
    setPermissions([]);
    setRoles([]);
    setIsAuthenticated(false);
  };

  const refreshAccessToken = async () => {
    const refreshed = await tryRefreshAccessToken();
    if (!refreshed) {
      logout();
      return false;
    }
    return true;
  };

  const hasPermission = (screen, action) => {
    if (!isAuthenticated) return false;
    if (user?.is_superuser) return true;
    
    const permissionKey = `${screen}:${action}`;
    return permissions.includes(permissionKey);
  };

  const hasRole = (roleName) => {
    if (!isAuthenticated) return false;
    return roles.includes(roleName);
  };

  const changePassword = async (currentPassword, newPassword) => {
    const accessToken = localStorage.getItem('accessToken');
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Password change failed');
      }

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const value = {
    user,
    permissions,
    roles,
    isAuthenticated,
    loading,
    login,
    logout,
    hasPermission,
    hasRole,
    refreshAccessToken,
    changePassword
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

